import { type JSX, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';

import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';

/** Utility type to extract param names from a path template. */
type ExtractParams<Path extends string> = Path extends `${string}:${infer Param}/${infer Rest}` ? Param | ExtractParams<Rest> : Path extends `${string}:${infer Param}` ? Param : never;

/** Utility type to create a record type from the params present in a path template. */
type ParamsFromPath<Path extends string> = {
    [K in ExtractParams<Path>]: string;
};

interface PageDataLoaderProps<Data, Path extends string> {
    /** The path for which the page is being rendered. This should be a constant from `PathTemplates.ts`. */
    path: Path;
    /** A function that fetches all initial data for the page, given the values for the params in the page's path. */
    fetchData: (urlParams: ParamsFromPath<Path>) => Promise<Data>;
    /** A function that renders the page after all initial data is fetched. */
    renderForm: (data: Data, urlParams: ParamsFromPath<Path>) => JSX.Element;
}

/**
 * Standardizes the loading of initial data for a page, including:
 * * Showing our Placeholder while data is loading.
 * * Showing an error message if fetching initial data fails.
 *
 * Similar to React Router's loaders, which we have not implemented (and may not want to).
 *
 * TODO: Over time, update existing pages to use this component for the sake of UI consistency and developer convenience.
 */
export const PageDataLoader = <Data, Path extends string>(props: PageDataLoaderProps<Data, Path>) => {
    const { fetchData, renderForm } = props;
    const params = useParams() as ParamsFromPath<Path>;
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState(false);
    const [data, setData] = useState<Data>();

    /**
     * Used as a useEffect dependency.
     * The intent of this component is for _initial_ data to be fetched; downstream components should refresh data themselves as needed.
     * Neither `params` nor `fetchData` can be depended on directly, because both have a new identity after any change to the URL, including a change to only the fragment or the query string.
     * Depending on them directly would cause every page built on this component to refetch all of its data when, for example, the user selected a new tab, updating the URL fragment.
     */
    const paramsKey = JSON.stringify(params);
    const latest = useRef({ fetchData, params });
    latest.current = { fetchData, params };

    useEffect(() => {
        const _fetchData = async () => {
            try {
                const response = await latest.current.fetchData(latest.current.params);
                setData(response);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };

        _fetchData();
    }, [paramsKey]);

    if (loadingErrorOccurred) {
        // TODO: This is how we have historically handled failures fetching initial data, but this looks terrible. Improve.
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (data === undefined) {
        return <Placeholder />;
    } else {
        return renderForm(data, params);
    }
};
